import { Injectable, NgModule } from '@angular/core';
import { Actions, EffectsModule, createEffect, ofType } from '@ngrx/effects';import { concatLatestFrom } from '@ngrx/operators';

import { Action, ReducerManager, Store, createAction, createSelector, on, props } from '@ngrx/store';
import { isNil } from 'lodash';
import { PagedResponse, QueryFiltersParams, QueryPagingParams } from 'mbs-ui-shared@models/Paging';
import { AppsUser, DomainInfo, DomainStatisticsFilterConverted } from 'mbs-ui-shared@models/domain.model';
import { DomainsService } from 'mbs-ui-shared@services/domains.service';
import { Observable, catchError, filter, map, mergeMap, of } from 'rxjs';
import { PagingStoreFactory } from '../pagingStoreFactory/pagingStoreFactory';
import { PagingActions, PagingSelectors } from '../pagingStoreFactory/pagingStoreTypes';
import { StoreTypes } from '../storeTypes.enum';
import {
  CustomDomainsStoreProperties,
  DomainsCustomActionTypes,
  DomainsCustomSelectorTypes,
  DomainsStoreCustomActionsType,
  DomainsStoreCustomActionsTypes
} from './domainsStore.types';

@Injectable({
  providedIn: 'root'
})
export class DomainsStore extends PagingStoreFactory<DomainInfo, CustomDomainsStoreProperties> {
  protected storeType = StoreTypes.Domains;
  protected selectId = (entity: DomainInfo): string => {
    return entity.id;
  };

  protected getEntityById(id: string): Observable<DomainInfo> {
    return this.domainsService.getById(id);
  }
  protected getEntities(params: QueryFiltersParams & QueryPagingParams): Observable<PagedResponse<DomainInfo>> {
    return this.domainsService.get(params);
  }

  constructor(
    public actions$: Actions,
    public store: Store,
    public reducerManager: ReducerManager,
    private domainsService: DomainsService
  ) {
    super(actions$, store, reducerManager);
    this.initStore();
  }

  // custom section
  // custom initial state
  protected getCustomInitialState() {
    return {
      custom: {
        restrictedNames: [],
        loadedRestrictedNames: false,
        domainStatistic: null,
        loadedDomainStatistic: false,
        loadingDomainStatistic: false
      } as CustomDomainsStoreProperties
    };
  }

  // custom types
  public actions: PagingActions<DomainInfo> & DomainsCustomActionTypes;
  public selectors: PagingSelectors<DomainInfo, CustomDomainsStoreProperties> & DomainsCustomSelectorTypes;

  private getCustomActionType = (actionType: DomainsStoreCustomActionsTypes): DomainsStoreCustomActionsType =>
    `[${this.storeType}] ${actionType}`;

  // custom actions
  protected prepareCustomActions(): void {
    this.actions.loadRestrictedNames = createAction(
      this.getCustomActionType(DomainsStoreCustomActionsTypes.loadRestrictedNames),
      props<{ force?: boolean }>()
    );
    this.actions.setRestrictedNames = createAction(
      this.getCustomActionType(DomainsStoreCustomActionsTypes.setRestrictedNames),
      props<{ restrictedNames: string[] }>()
    );
    this.actions.loadDomainStatistic = createAction(
      this.getCustomActionType(DomainsStoreCustomActionsTypes.loadDomainStatistic),
      props<{ force?: boolean }>()
    );
    this.actions.setDomainStatistic = createAction(
      this.getCustomActionType(DomainsStoreCustomActionsTypes.setDomainStatistic),
      props<{ domainStatistic: DomainStatisticsFilterConverted }>()
    );
  }

  // custom selectors
  protected prepareCustomSelectors(): void {
    this.selectors.selectRestrictedNames = createSelector(this.selectors.featureSelector, (state) => state.custom?.restrictedNames ?? []);
    this.selectors.selectLoadedRestrictedNames = createSelector(
      this.selectors.featureSelector,
      (state) => state.custom?.loadedRestrictedNames ?? false
    );
    this.selectors.selectDomainStatistic = createSelector(this.selectors.featureSelector, (state) => state.custom?.domainStatistic ?? null);
    this.selectors.selectLoadedDomainStatistic = createSelector(
      this.selectors.featureSelector,
      (state) => state.custom?.loadedDomainStatistic ?? false
    );
    this.selectors.selectLoadingDomainStatistic = createSelector(
      this.selectors.featureSelector,
      (state) => state.custom?.loadingDomainStatistic ?? false
    );
  }

  // custom reducer
  protected prepareCustomReducer(): void {
    this.customReducer = [
      on(this.actions.setRestrictedNames, (state, action) => {
        state.custom.restrictedNames = action.restrictedNames;
        state.custom.loadedRestrictedNames = true;

        return state;
      }),
      on(this.actions.loadDomainStatistic, (state, action) => {
        (action.force || !state.custom.loadedDomainStatistic) && (state.custom.loadingDomainStatistic = true);

        return state;
      }),
      on(this.actions.setDomainStatistic, (state, action) => {
        state.custom.domainStatistic = action.domainStatistic;
        state.custom.loadedDomainStatistic = !isNil(action.domainStatistic);
        state.custom.loadingDomainStatistic = false;

        return state;
      })
    ];
  }

  // custom effects
  public loadRestrictedNames$: Observable<Action>;
  public loadDomainStatistic$: Observable<Action>;

  protected prepareCustomEffects(): void {
    this.loadRestrictedNames$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(this.actions.loadRestrictedNames),
        concatLatestFrom(() => this.store.select(this.selectors.selectLoadedRestrictedNames)),
        filter(([action, loaded]) => action.force || !loaded),
        mergeMap(() => {
          return this.domainsService
            .getRestrictedNames()
            .pipe(map((result) => this.actions.setRestrictedNames({ restrictedNames: result })));
        })
      );
    });
    this.loadDomainStatistic$ = createEffect(() => {
      return this.actions$.pipe(
        ofType(this.actions.loadDomainStatistic),
        concatLatestFrom(() => this.store.select(this.selectors.selectLoadedDomainStatistic)),
        filter(([action, loaded]) => action.force || !loaded),
        mergeMap(() => {
          return this.domainsService.getDomainStatistic().pipe(
            map((result) => this.actions.setDomainStatistic({ domainStatistic: this.convertStatistics(result) })),
            catchError(() => of(this.actions.setDomainStatistic({ domainStatistic: null })))
          );
        })
      );
    });
  }

  private convertStatistics = (stat: PagedResponse<AppsUser>): DomainStatisticsFilterConverted => ({
    total: stat?.total,
    ...stat?.data?.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.status]: { count: curr.count }
      }),
      {}
    )
  });
}

@NgModule({
  imports: [EffectsModule.forFeature([DomainsStore])]
})
export class DomainsStoreModule {}
